import {
  GroupData,
  InspectModel,
  UndisplayableGroup,
} from '@volkswagen-onehub/car-technical-details-bff';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@volkswagen-onehub/components-core';
import * as React from 'react';

export const UndisplaybleGroupInformation = (props: {
  undisplayableGroup?: UndisplayableGroup;
}): React.JSX.Element | null => {
  const {undisplayableGroup} = props;
  if (!undisplayableGroup) {
    return null;
  }
  const groupDatas = createUndisplaybleGroupData(undisplayableGroup);

  return (
    <>
      <h2>Data Issues</h2>
      <Text>
        Groups in which models with the same filter exhibit different data.
      </Text>
      {groupDatas &&
        groupDatas.map((groupData) => (
          <>
            <h3>{groupData.key}</h3>
            <Table>
              <TableHead>
                <TableRow key="uniqueFilter">
                  {groupData.uniqueFilters.map((uniqueFilter) => {
                    return (
                      <TableHeader emphasized key={uniqueFilter}>
                        {uniqueFilter}
                      </TableHeader>
                    );
                  })}
                </TableRow>

                {groupData.filters.map((filters, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableHeader emphasized key={filters.key}>
                        {filters.key}
                      </TableHeader>
                      {filters.values.map((model) => {
                        return (
                          <TableHeader emphasized key={model}>
                            {model}
                          </TableHeader>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableHead>
              <TableBody>
                {groupData.values.map((values, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableCell key={values.key}>{values.key}</TableCell>
                      {values.values.map((model) => {
                        return <TableCell key={model}>{model}</TableCell>;
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        ))}
    </>
  );
};

interface ProcessedNoSubsetForGroup {
  key: string;
  uniqueFilters: string[];
  filters: Row[];
  values: Row[];
}

interface Row {
  key: string;
  values: string[];
}

function createUndisplaybleGroupData(
  undisplaybleGroup: UndisplayableGroup
): ProcessedNoSubsetForGroup[] {
  return undisplaybleGroup.map((originalGroup) => {
    originalGroup.models.sort((m1, m2) => {
      const values1 = Object.values(m1.filters).join(',');
      const values2 = Object.values(m2.filters).join(',');
      return values1.localeCompare(values2);
    });

    const filters: Row[] = createRow(originalGroup, (model) => model.filters);
    const uniqueFilterIndexes = createUniqueFilterIndexes(originalGroup);

    const values: Row[] = createRow(originalGroup, (model) => model.values);

    return {
      key: originalGroup.key,
      uniqueFilters: uniqueFilterIndexes,
      filters,
      values,
    };
  });
}
function createRow(
  originalGroup: GroupData,
  data: (model: InspectModel) => Record<string, string>
) {
  const filterKeys = originalGroup.models.reduce((keys, model) => {
    Object.keys(data(model)).forEach((k) => {
      if (!keys.includes(k)) {
        keys.push(k);
      }
    });

    return keys;
  }, [] as string[]);
  const filters: Row[] = filterKeys.map((key) => {
    const values = originalGroup.models.map((model) => {
      return data(model)[key];
    });
    return {
      key,
      values,
    } as Row;
  });
  return filters;
}

function createUniqueFilterIndexes(originalGroup: GroupData) {
  const uniqueFilters = originalGroup.models.map((model) =>
    Object.values(model.filters).join(',')
  );
  const filterToIndex = uniqueFilters.reduce((uniqueIndexes, filter) => {
    if (!uniqueIndexes[filter]) {
      uniqueIndexes[filter] = Object.keys(uniqueIndexes).length;
    }
    return uniqueIndexes;
  }, {} as Record<string, number>);
  const uniqueFilterIndexes = [''].concat(
    uniqueFilters.map((filter) => String(filterToIndex[filter]))
  );
  return uniqueFilterIndexes;
}
